import { useEffect, useState, Fragment } from "react";
import { GoogleFeedAd } from "@/components/GoogleAd";
import { QueryState } from "@/components/QueryState";
import { useListFreeItems, useCreateFreeItem, useGetMyFreeItems, useDeleteFreeItem, useMarkFreeItemCollected } from "@workspace/api-client-react";
import { useSEO } from "@/hooks/use-seo";
import { useAuth } from "@/hooks/use-auth";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { Gift, X, MapPin, Tag, User, Clock, ArrowLeft, MessageCircle, Trash2, CheckCircle, List } from "lucide-react";

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem("ayrshire_token");
  return token
    ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
    : { "Content-Type": "application/json" };
}

const SPONSOR_WEEKS = [
  { weeks: 1, label: "1 week" },
  { weeks: 2, label: "2 weeks" },
  { weeks: 4, label: "4 weeks" },
] as const;

function FreeItemSponsorButton({ id, sponsored, onDone }: { id: number; sponsored?: boolean; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [prices, setPrices] = useState<Record<number, number>>({});
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [sponsoring, setSponsoring] = useState<number | null>(null);

  const loadPrices = async () => {
    setLoadingPrices(true);
    try {
      const results = await Promise.all(
        [1, 2, 4].map(async (w) => {
          const res = await fetch(`/api/stripe/price?type=sponsored_free_item_${w}w`, { headers: authHeaders() });
          const data = await res.json() as { price: number };
          return [w, data.price] as const;
        })
      );
      setPrices(Object.fromEntries(results));
    } finally {
      setLoadingPrices(false);
    }
  };

  const handleOpen = () => { setOpen(true); void loadPrices(); };

  const handleSponsor = async (weeks: number) => {
    setSponsoring(weeks);
    try {
      const price = prices[weeks] ?? 0;
      if (price > 0) {
        const checkoutRes = await fetch("/api/stripe/checkout", {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({
            amount: price,
            description: `Boost listing — ${weeks} week${weeks > 1 ? "s" : ""} — Ayrshire Hub`,
            successUrl: `${window.location.origin}${window.location.pathname}?free_item_sponsor_success=1&free_item_id=${id}&sponsor_weeks=${weeks}&session_id={CHECKOUT_SESSION_ID}`,
            cancelUrl: window.location.href,
            metadata: { type: `sponsored_free_item_${weeks}w`, freeItemId: String(id) },
          }),
        });
        const checkout = await checkoutRes.json() as { url?: string; error?: string };
        if (checkout.url) { window.location.href = checkout.url; return; }
        throw new Error(checkout.error ?? "Payment setup failed");
      }
      await fetch(`/api/free-items/${id}/sponsor`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ weeks }),
      });
      setOpen(false);
      onDone();
    } finally {
      setSponsoring(null);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("free_item_sponsor_success") === "1") {
      const itemId = params.get("free_item_id");
      const weeks = params.get("sponsor_weeks");
      const sessionId = params.get("session_id");
      if (itemId === String(id) && weeks && sessionId) {
        window.history.replaceState({}, "", window.location.pathname);
        void fetch(`/api/free-items/${id}/sponsor`, {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({ weeks: Number(weeks), stripeSessionId: sessionId }),
        }).then(() => onDone());
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (sponsored) {
    return (
      <span className="text-xs font-semibold text-amber-700 border border-amber-300 bg-amber-50 dark:bg-amber-900/20 px-2 py-1 rounded">
        ⭐ Boosted
      </span>
    );
  }

  if (!open) {
    return (
      <button
        onClick={handleOpen}
        className="text-xs font-semibold text-amber-700 border border-amber-300 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 px-2 py-1 rounded transition-colors flex items-center gap-1"
      >
        ⭐ Boost
      </button>
    );
  }

  return (
    <div className="flex flex-col gap-1 border border-amber-200 bg-amber-50 dark:bg-amber-900/20 rounded-xl p-3 min-w-[200px]">
      <p className="text-xs font-semibold text-amber-800 dark:text-amber-300 mb-1">⭐ Boost this listing</p>
      {loadingPrices ? (
        <p className="text-xs text-slate-500">Loading prices…</p>
      ) : (
        <div className="flex flex-col gap-1">
          {SPONSOR_WEEKS.map(({ weeks, label }) => (
            <button
              key={weeks}
              onClick={() => void handleSponsor(weeks)}
              disabled={sponsoring !== null}
              className="flex items-center justify-between text-xs font-medium bg-white border border-amber-200 hover:border-amber-400 hover:bg-amber-50 dark:bg-amber-900/20 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
            >
              <span>{label}</span>
              <span className="text-amber-700 font-semibold">
                {(prices[weeks] ?? 0) === 0 ? "Free" : `£${((prices[weeks] ?? 0) / 100).toFixed(2)}`}
              </span>
            </button>
          ))}
        </div>
      )}
      <button
        onClick={() => setOpen(false)}
        className="text-xs text-slate-500 hover:text-slate-700 mt-1 text-left"
      >
        Cancel
      </button>
    </div>
  );
}
import { useCategories } from "@/hooks/use-categories";
import { SidebarFilter } from "@/components/SidebarFilter";
import { timeAgo } from "@/lib/time-ago";
import { AYRSHIRE_TOWNS, useTown } from "@/hooks/use-town";
import { ImageUpload } from "@/components/ImageUpload";

const TOWNS = AYRSHIRE_TOWNS.filter(t => t !== "All Towns");

const CONDITION_STYLES: Record<string, string> = {
  "Like New": "bg-emerald-100 text-emerald-700",
  "Good": "bg-blue-100 text-blue-700",
  "Used": "bg-amber-100 text-amber-700",
  "Poor": "bg-red-100 text-red-700",
};

const CONDITIONS = ["Like New", "Good", "Used", "Poor"];

interface FreeItem {
  id: number;
  title: string;
  description?: string | null;
  category?: string | null;
  town?: string | null;
  condition?: string | null;
  contactName?: string | null;
  contactPhone?: string | null;
  imageUrl?: string | null;
  collected?: boolean | null;
  hasOwner?: boolean;
  listingType?: "free" | "sale" | null;
  price?: number | null;
  createdAt?: string;
  sponsored?: boolean | null;
  sponsoredUntil?: string | null;
}

function ItemDetailModal({ item, onClose, onEnquire }: { item: FreeItem; onClose: () => void; onEnquire: () => void }) {
  const condStyle = CONDITION_STYLES[item.condition ?? ""] ?? "bg-slate-100 text-slate-600";
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col">
        {item.imageUrl ? (
          <div className="relative">
            <img src={item.imageUrl} alt={item.title} className="w-full h-56 object-cover" />
            <button
              onClick={onClose}
              className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/40 flex items-center justify-center text-white backdrop-blur-sm"
            >
              <X size={18} />
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
                <ArrowLeft size={20} />
              </button>
              <span className="font-bold text-slate-800">Item Details</span>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
              <X size={20} />
            </button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="flex items-start justify-between gap-3">
            <h2 className="text-xl font-bold text-slate-800 leading-tight">{item.title}</h2>
            {item.condition && (
              <span className={`flex-shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full ${condStyle}`}>
                {item.condition}
              </span>
            )}
          </div>

          <div className="flex flex-wrap gap-3 text-sm text-slate-500">
            {item.town && (
              <span className="flex items-center gap-1.5">
                <MapPin size={14} className="text-slate-400" />
                {item.town}
              </span>
            )}
            {item.category && (
              <span className="flex items-center gap-1.5">
                <Tag size={14} className="text-slate-400" />
                <span className="capitalize">{item.category}</span>
              </span>
            )}
            {item.contactName && (
              <span className="flex items-center gap-1.5">
                <User size={14} className="text-slate-400" />
                {item.contactName}
              </span>
            )}
            {item.createdAt && (
              <span className="flex items-center gap-1.5">
                <Clock size={14} className="text-slate-400" />
                {timeAgo(item.createdAt)}
              </span>
            )}
          </div>

          {item.description && (
            <div className="bg-slate-50 rounded-2xl p-4">
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{item.description}</p>
            </div>
          )}

          {item.collected ? (
            <div className="flex items-center justify-center gap-2 bg-slate-100 rounded-2xl py-3 text-slate-500 text-sm font-semibold">
              This item has already been collected
            </div>
          ) : item.hasOwner ? (
            <button
              onClick={onEnquire}
              className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold py-4 rounded-2xl transition-colors"
            >
              <MessageCircle size={18} />
              Message About This Item
            </button>
          ) : item.contactPhone ? (
            <a
              href={`tel:${item.contactPhone}`}
              className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-4 rounded-2xl transition-colors"
            >
              📞 Call {item.contactName ?? "Owner"}
            </a>
          ) : (
            <div className="text-center text-sm text-slate-400 py-2">
              Contact: {item.contactName}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MyListingsModal({ onClose }: { onClose: () => void }) {
  const { data, isLoading } = useGetMyFreeItems();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const { mutate: doDelete, isPending: deleting } = useDeleteFreeItem({
    mutation: {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: ["/api/free-items/mine"] });
        void qc.invalidateQueries({ queryKey: ["/api/free-items"] });
        toast({ title: "Item removed" });
        setConfirmDeleteId(null);
      },
      onError: () => toast({ title: "Could not delete item", variant: "destructive" }),
    },
  });

  const { mutate: doCollected, isPending: collecting } = useMarkFreeItemCollected({
    mutation: {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: ["/api/free-items/mine"] });
        void qc.invalidateQueries({ queryKey: ["/api/free-items"] });
        toast({ title: "Marked as collected!" });
      },
      onError: () => toast({ title: "Could not update item", variant: "destructive" }),
    },
  });

  const myItems = (data?.items ?? []) as FreeItem[];
  const available = myItems.filter((i) => !i.collected);
  const collected = myItems.filter((i) => i.collected);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <span className="font-bold text-slate-800 text-base">My Listings</span>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="p-8 text-center text-slate-400 text-sm">Loading your items…</div>
          ) : myItems.length === 0 ? (
            <div className="p-8 text-center space-y-2">
              <Gift size={32} className="text-slate-300 mx-auto" />
              <p className="text-slate-500 font-semibold text-sm">You haven't listed anything yet</p>
              <p className="text-slate-400 text-xs">Post a free item and it'll show up here.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-50">
              {available.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-5 pt-4 pb-2">Available ({available.length})</p>
                  {available.map((item) => (
                    <div key={item.id} className="px-5 py-3 flex items-start gap-3">
                      {item.imageUrl && (
                        <img src={item.imageUrl} alt={item.title} className="w-12 h-12 rounded-xl object-cover flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-800 text-sm truncate">{item.title}</p>
                        <p className="text-xs text-slate-400 mt-0.5">{item.town}{item.condition ? ` · ${item.condition}` : ""}</p>
                        {item.createdAt && <p className="text-xs text-slate-400">{timeAgo(item.createdAt)}</p>}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                        <FreeItemSponsorButton
                          id={item.id}
                          sponsored={!!item.sponsored && (!item.sponsoredUntil || new Date(item.sponsoredUntil) > new Date())}
                          onDone={() => {
                            void qc.invalidateQueries({ queryKey: ["/api/free-items/mine"] });
                            void qc.invalidateQueries({ queryKey: ["/api/free-items"] });
                            toast({ title: "Boost activated!", description: "Your listing is now pinned to the top." });
                          }}
                        />
                        {confirmDeleteId === item.id ? (
                          <>
                            <button
                              onClick={() => doDelete({ id: item.id })}
                              disabled={deleting}
                              className="text-xs font-semibold text-red-600 hover:text-red-700 px-2 py-1 rounded-lg bg-red-50"
                            >
                              Confirm
                            </button>
                            <button
                              onClick={() => setConfirmDeleteId(null)}
                              className="text-xs text-slate-400 px-2 py-1"
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => doCollected({ id: item.id })}
                              disabled={collecting}
                              title="Mark as collected"
                              className="flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-2.5 py-1.5 rounded-lg transition-colors"
                            >
                              <CheckCircle size={13} />
                              Collected
                            </button>
                            <button
                              onClick={() => setConfirmDeleteId(item.id)}
                              title="Delete listing"
                              className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <Trash2 size={14} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {collected.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-5 pt-4 pb-2">Collected ({collected.length})</p>
                  {collected.map((item) => (
                    <div key={item.id} className="px-5 py-3 flex items-start gap-3 opacity-50">
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-600 text-sm line-through truncate">{item.title}</p>
                        <p className="text-xs text-slate-400">{item.town}</p>
                      </div>
                      <button
                        onClick={() => doDelete({ id: item.id })}
                        disabled={deleting}
                        title="Remove"
                        className="w-7 h-7 flex items-center justify-center text-slate-300 hover:text-red-400 transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FreeItemsSkeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div key={i} className="bg-white rounded-2xl p-4 shadow-sm animate-pulse">
          <div className="h-4 bg-slate-200 rounded mb-3" />
          <div className="h-3 bg-slate-200 rounded mb-2 w-1/3" />
          <div className="h-3 bg-slate-200 rounded w-2/3" />
        </div>
      ))}
    </div>
  );
}

export default function FreeItems() {
  useSEO({ title: "Marketplace — Free & For Sale in Ayrshire", description: "Browse free items and local for-sale listings across Ayrshire. Give, receive, and buy locally in Ayr, Kilmarnock, Irvine and more." });
  const [selectedCat, setSelectedCat] = useState("All");
  const [selectedType, setSelectedType] = useState<"" | "free" | "sale">("");
  const categories = ["All", ...useCategories("free_item")];
  const { townParam } = useTown();
  const { data: freeData, isLoading, isError, refetch } = useListFreeItems({
    ...(townParam ? { town: townParam } : {}),
    ...(selectedCat !== "All" ? { category: selectedCat } : {}),
    ...(selectedType ? { listingType: selectedType } : {}),
  } as Parameters<typeof useListFreeItems>[0]);
  const items = (freeData?.items ?? []) as FreeItem[];
  const createItem = useCreateFreeItem();
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [showMyListings, setShowMyListings] = useState(false);
  const [selectedItem, setSelectedItem] = useState<FreeItem | null>(null);
  const [formData, setFormData] = useState({
    title: "", description: "", category: "", town: "Ayr", condition: "Good", contactName: "",
  });
  const [imageUrl, setImageUrl] = useState<string | undefined>(undefined);

  const handleOpenComposer = () => {
    if (!user) {
      toast({ title: "Sign in required", description: "Please sign in or create a free account to post items." });
      setLocation("/login");
      return;
    }
    setOpen(true);
  };

  const handleEnquire = (item: FreeItem) => {
    if (!user) { setLocation("/login"); return; }
    setLocation(`/messages?freeItemId=${item.id}&itemTitle=${encodeURIComponent(item.title)}`);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await createItem.mutateAsync({ data: { ...formData, ...(imageUrl ? { imageUrl } : {}) } as never });
    setOpen(false);
    setFormData({ title: "", description: "", category: "", town: "Ayr", condition: "Good", contactName: "" });
    setImageUrl(undefined);
    refetch();
    toast({ title: "Item posted!", description: "Your item is now live in the community exchange." });
  };

  const available = items.filter((i) => !i.collected);
  const collected = items.filter((i) => i.collected);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-100">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <p className="text-sm text-slate-500 mb-1">Free &amp; for sale — local listings</p>
              <h1 className="text-3xl font-bold text-primary">Marketplace</h1>
            </div>
            <div className="flex items-center gap-2">
              {user && (
                <button
                  onClick={() => setShowMyListings(true)}
                  className="flex items-center gap-2 border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 font-semibold px-4 py-2.5 rounded-xl transition-colors"
                >
                  <List size={16} />
                  My Listings
                </button>
              )}
              <button
                onClick={handleOpenComposer}
                className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-semibold px-5 py-2.5 rounded-xl transition-colors"
              >
                <Gift size={18} />
                List an Item
              </button>
            </div>
          </div>

        </div>
      </div>

      <SidebarFilter items={categories} selected={selectedCat} onSelect={setSelectedCat}>
        {/* Type filter strip */}
        <div className="border-b border-slate-200 bg-white">
          <div className="flex gap-2 overflow-x-auto px-4 py-2.5" style={{ scrollbarWidth: "none" }}>
            {[{ value: "" as const, label: "All" }, { value: "free" as const, label: "Free" }, { value: "sale" as const, label: "For Sale" }].map(({ value, label }) => (
              <button key={label} onClick={() => setSelectedType(value)} className={`flex-shrink-0 px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-all ${value === selectedType ? value === "sale" ? "bg-amber-500 text-white border-amber-500" : "bg-emerald-600 text-white border-emerald-600" : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"}`}>
                {label}
              </button>
            ))}
            {items.length > 0 && (
              <span className="ml-auto self-center text-xs text-emerald-700 font-semibold flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />
                {available.length} available
              </span>
            )}
          </div>
        </div>
        <div className="px-4 py-8">
        <QueryState
          isLoading={isLoading}
          isError={isError}
          isEmpty={items.length === 0}
          onRetry={refetch}
          skeleton={<FreeItemsSkeleton />}
          errorTitle="Couldn't load the marketplace"
          errorBody="We couldn't reach Ayrshire Hub just now. Check your connection and try again."
          emptyIcon={<Gift size={28} className="text-primary" />}
          emptyTitle="Nothing listed yet."
          emptyBody="Be the first to give something away in your community!"
          emptyAction={{ label: "Post the First Item", onClick: handleOpenComposer }}
        >
          <div className="space-y-8">
            {available.length > 0 && (
              <div>
                <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Available Items</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {available.map((item, i) => {
                    const condStyle = CONDITION_STYLES[item.condition ?? ""] ?? "bg-slate-100 text-slate-600";
                    return (
                      <Fragment key={item.id}>
                      <button
                        onClick={() => setSelectedItem(item)}
                        className="bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all text-left w-full overflow-hidden"
                      >
                        {item.imageUrl && (
                          <img src={item.imageUrl} alt={item.title} className="w-full h-36 object-cover" />
                        )}
                        <div className="p-4">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <h3 className="font-bold text-slate-800">{item.title}</h3>
                            {item.condition && (
                              <span className={`flex-shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full ${condStyle}`}>{item.condition}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 text-xs text-slate-400 mb-2">
                            <span>📍</span>
                            <span>{item.town}</span>
                            {item.category && (
                              <>
                                <span>·</span>
                                <span className="capitalize">{item.category}</span>
                              </>
                            )}
                            {item.createdAt && (
                              <>
                                <span>·</span>
                                <span>{timeAgo(item.createdAt)}</span>
                              </>
                            )}
                          </div>
                          <p className="text-slate-600 text-sm line-clamp-2 mb-3">{item.description}</p>
                          <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                            <span className="text-xs text-slate-400 flex items-center gap-1">
                              👤 {item.contactName}
                            </span>
                            {item.listingType === "sale" && item.price != null ? (
                              <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-700 bg-amber-100 px-2.5 py-1 rounded-full">
                                £{(item.price / 100).toFixed(2)}
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                                FREE
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                      {i === 2 && <div className="col-span-full"><GoogleFeedAd /></div>}
                      </Fragment>
                    );
                  })}
                </div>
              </div>
            )}
            {collected.length > 0 && (
              <div>
                <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">Collected Items</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 opacity-50">
                  {collected.map((item) => (
                    <div key={item.id} className="bg-white rounded-2xl border border-slate-100 p-4">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h3 className="font-bold text-slate-600 line-through">{item.title}</h3>
                        <span className="flex-shrink-0 text-[11px] font-semibold bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">Collected</span>
                      </div>
                      <p className="text-xs text-slate-400">{item.town}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </QueryState>
        </div>
      </SidebarFilter>

      {selectedItem && (
        <ItemDetailModal
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
          onEnquire={() => { setSelectedItem(null); handleEnquire(selectedItem); }}
        />
      )}

      {showMyListings && <MyListingsModal onClose={() => setShowMyListings(false)} />}

      {open && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl max-h-[85vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <span className="font-bold text-slate-800 text-base">List an Item</span>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600">
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">Item Title</label>
                  <input
                    required
                    placeholder="e.g. 3-seater sofa"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-800 placeholder:text-slate-400 outline-none focus:border-primary transition-colors"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">Description</label>
                  <textarea
                    required
                    rows={3}
                    placeholder="Describe the item, any faults, collection instructions..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-800 placeholder:text-slate-400 outline-none focus:border-primary transition-colors resize-none"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">Town</label>
                    <select
                      required
                      value={formData.town}
                      onChange={(e) => setFormData({ ...formData, town: e.target.value })}
                      className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-800 outline-none focus:border-primary transition-colors bg-white"
                    >
                      {TOWNS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">Category</label>
                    <select
                      required
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-800 outline-none focus:border-primary transition-colors bg-white"
                    >
                      <option value="">Select…</option>
                      {categories.filter(c => c !== "All").map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">Condition</label>
                  <div className="grid grid-cols-4 gap-2">
                    {CONDITIONS.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setFormData({ ...formData, condition: c })}
                        className="py-2 px-1 rounded-xl border text-xs font-semibold transition-colors"
                        style={{
                          backgroundColor: formData.condition === c ? "#1e3a5f" : "var(--color-card)",
                          borderColor: formData.condition === c ? "#1e3a5f" : "#e2e8f0",
                          color: formData.condition === c ? "var(--color-card)" : "#64748b",
                        }}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">Your Name</label>
                  <input
                    required
                    placeholder="First name only is fine"
                    value={formData.contactName}
                    onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                    className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-800 placeholder:text-slate-400 outline-none focus:border-primary transition-colors"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">Photo (optional)</label>
                  <ImageUpload value={imageUrl ?? ""} onChange={(url) => setImageUrl(url || undefined)} label="Add a photo" />
                </div>
                <button
                  type="submit"
                  disabled={createItem.isPending}
                  className="w-full bg-primary hover:bg-primary/90 disabled:opacity-70 text-white font-bold py-4 rounded-2xl transition-all"
                >
                  {createItem.isPending ? "Posting..." : "Post Item"}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
